import {
  AttachmentParseStatus,
  SupplierThread,
  SupplierThreadStatus,
  ThreadAttachment,
  ThreadMessage,
} from '../components/forms/outreach/types';
import { componentKeyOf, isSupplierShortlisted, normalizeSupplier } from '../components/discovery/discoveryModel';
import { isCandidateShortlisted } from '../utils/supplierCandidates';
import { formatPrice } from '../utils/currency';

/**
 * Pure mapping behind the outreach hook: BOM research + conversation state →
 * supplier threads. No React, no HTTP. Every thread field traces to stored
 * data — missing facts stay undefined and the UI omits them (honest-UI rule).
 *
 * Which suppliers earn a thread is decided by utils/supplierCandidates, the
 * same predicate the journey rail's readiness uses — a thread on this step and
 * a checkmark on step 3 are the same statement about the same data.
 */

const RESPONDED_STATUSES = ['RESPONDED'];
const AWAITING_STATUSES = ['AWAITING_REPLY', 'CONTACTED'];

/**
 * Where the conversation stands, read off the two places the server records
 * it. A failed send is checked first — it is the one state the buyer has to
 * act on — and negotiating outranks a plain wait because it says which kind
 * of silence this is.
 */
const resolveThreadStatus = (candidate: any, latestConversation: any): SupplierThreadStatus => {
  const candidateStatus = candidate?.status;
  const conversationStatus = latestConversation?.status;
  if (conversationStatus === 'FAILED') return 'failed';
  if (RESPONDED_STATUSES.includes(candidateStatus) || RESPONDED_STATUSES.includes(conversationStatus)) {
    return 'replied';
  }
  if (candidateStatus === 'IN_NEGOTIATION') return 'negotiating';
  if (AWAITING_STATUSES.includes(candidateStatus) || AWAITING_STATUSES.includes(conversationStatus)) {
    return 'awaiting_reply';
  }
  return 'not_sent';
};

/** Robbie's one-liner when the conversation itself has no snippet to show. */
const STATUS_SUMMARY: Record<SupplierThreadStatus, string> = {
  not_sent: 'I haven’t asked them anything yet',
  awaiting_reply: 'Ask sent — no reply yet',
  negotiating: 'Counter on the table — no reply yet',
  replied: 'Reply received',
  failed: 'The last send didn’t leave the platform',
};

/**
 * The send payload: the buyer's query plus the stage context Robbie composes
 * from. It carries no subject, body or recipient address — the buyer never
 * writes the email and never handles the address, and the backend rejects a
 * payload that tries to supply either.
 */
export const buildOutreachPayload = (
  thread: SupplierThread,
  sourceQuery: string,
  isNegotiation: boolean,
) => ({
  supplierId: thread.id,
  supplierName: thread.supplierName,
  stage: isNegotiation ? ('NEGOTIATION' as const) : ('RFQ' as const),
  context: isNegotiation
    ? { buyerAsk: sourceQuery }
    : {
        supplierName: thread.supplierName,
        productName: thread.componentName,
        queries: sourceQuery,
      },
  sourceQuery,
});

/**
 * The stored outbound message is the email as one block, subject line first.
 * Split so the peek can show them as an email rather than as prose — an
 * unlabelled block is all body, which is what pre-subject sends look like.
 */
export const splitSentEmail = (text: string): { subject?: string; body: string } => {
  const match = /^Subject:\s*(.*?)\n\n([\s\S]*)$/.exec(text.trim());
  if (!match) return { body: text.trim() };
  return { subject: match[1].trim() || undefined, body: match[2].trim() };
};

/**
 * The component's shortlist with one supplier taken off — what remains after
 * removing that supplier's un-started thread (the batch-shortlist endpoint
 * receives the full remaining list per component).
 */
export const shortlistWithoutSupplier = (
  components: any[],
  componentId: string,
  supplierName: string,
): string[] => {
  const index = components.findIndex((c: any, i: number) => componentKeyOf(c, i) === componentId);
  const component = index >= 0 ? components[index] : null;
  return (component?.supplierCandidates || [])
    .filter((s: any) => isSupplierShortlisted(s))
    .map((s: any) => s.supplierName || s.name)
    .filter((name: string) => Boolean(name) && name !== supplierName);
};

/**
 * Threads with no server conversation yet get a stable local id. The `draft-`
 * prefix is an id namespace, not a label — it is what tells a local id from a
 * comms one, and it lives in URLs the buyer may have open.
 */
export const draftThreadId = (componentKey: string, supplierName: string): string =>
  `draft-${`${componentKey}-${supplierName}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

export const isRemoteThreadId = (id: string): boolean => !id.startsWith('draft-');

export const timeAgo = (iso?: string): string | undefined => {
  if (!iso) return undefined;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return undefined;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/** "Mon 10:04" within the last week, else a short date — matches the mock. */
export const formatMessageTime = (raw: string): string => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const days = (Date.now() - date.getTime()) / 86_400_000;
  if (days < 7) return `${date.toLocaleDateString(undefined, { weekday: 'short' })} ${time}`;
  return `${date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${time}`;
};

const PARSE_STATUSES: AttachmentParseStatus[] = ['PARSED', 'EMPTY', 'UNSUPPORTED', 'TOO_LARGE', 'SKIPPED', 'FAILED'];

/**
 * Attachment rows off the API, made safe to render. A row without a filename
 * cannot be shown honestly, so it is dropped rather than labelled "file";
 * an unrecognised parse status is left undefined, which the chip reads as
 * "nothing claimed" instead of inventing a failure.
 */
export const mapAttachments = (raw: any): ThreadAttachment[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const mapped = raw
    .filter((a: any) => a && typeof a.filename === 'string' && a.filename.trim())
    .map((a: any) => ({
      filename: a.filename.trim(),
      contentType: typeof a.contentType === 'string' ? a.contentType : undefined,
      parseStatus: PARSE_STATUSES.includes(a.parseStatus) ? (a.parseStatus as AttachmentParseStatus) : undefined,
    }));
  return mapped.length > 0 ? mapped : undefined;
};

/**
 * Ledger entries belonging to one conversation. Entries written before
 * provenance was recorded carry no threadId — they stay, because dropping a
 * real supplier movement is worse than showing it on the only thread there is.
 */
const ledgerForThread = <T extends { threadId?: string }>(entries: any, threadId: string): T[] | undefined => {
  if (!Array.isArray(entries) || entries.length === 0) return undefined;
  const mine = entries.filter((e: T) => !e?.threadId || e.threadId === threadId);
  return mine.length > 0 ? mine : undefined;
};

export const buildThreads = (
  components: any[],
  threadMessages: Record<string, ThreadMessage[]>,
): SupplierThread[] => {
  const list: SupplierThread[] = [];
  components.forEach((comp: any, compIdx: number) => {
    const componentKey = componentKeyOf(comp, compIdx);
    // One thread per supplier per line: its ticked products share one email and one thread.
    const shortlisted = (comp?.supplierCandidates || []).filter(isCandidateShortlisted);
    type SupplierRef = { supplierKey?: string; supplierName?: string; name?: string };
    const supplierOf = (sup: SupplierRef): string => sup?.supplierKey || sup?.supplierName || sup?.name || '';
    const productCounts = new Map<string, number>();
    shortlisted.forEach((sup: SupplierRef) => productCounts.set(supplierOf(sup), (productCounts.get(supplierOf(sup)) || 0) + 1));
    const seen = new Set<string>();
    shortlisted.forEach((sup: any) => {
      if (seen.has(supplierOf(sup))) return;
      seen.add(supplierOf(sup));
      const productCount = productCounts.get(supplierOf(sup)) || 1;
      const view = normalizeSupplier(sup);
      const latestConv =
        Array.isArray(sup?.conversations) && sup.conversations.length > 0
          ? sup.conversations[sup.conversations.length - 1]
          : null;
      const status = resolveThreadStatus(sup, latestConv);
      const threadId = latestConv?.threadId || draftThreadId(componentKey, view.name);

      list.push({
        id: threadId,
        componentId: componentKey,
        supplierName: view.name,
        ...(productCount > 1 ? { productCount } : {}),
        hasContactEmail: view.hasContactEmail,
        findingContact: view.findingContact,
        location: view.location,
        componentName: comp?.name || 'Component',
        quantity: typeof comp?.quantity === 'number' ? comp.quantity : undefined,
        targetPrice: typeof comp?.targetPrice === 'number' ? comp.targetPrice : undefined,
        targetPriceLabel: typeof comp?.targetPrice === 'number' ? `${formatPrice(comp.targetPrice, comp.currency || 'INR')} / unit` : undefined,
        status,
        summary: latestConv?.lastMessageSnippet || STATUS_SUMMARY[status],
        lastActivityIso: latestConv?.lastActivityAt || undefined,
        messages:
          threadMessages[threadId] ||
          (latestConv
            ? [
                {
                  id: `${threadId}-snippet`,
                  sender: 'buyer' as const,
                  senderName: 'You',
                  timestamp: latestConv.lastActivityAt ? formatMessageTime(latestConv.lastActivityAt) : 'Sent',
                  text: latestConv.lastMessageSnippet || 'Message sent',
                },
              ]
            : []),
        trustScore: view.trustScore,
        leadTimeLabel: view.leadTimeLabel,
        aiReasoning: view.reason,
        quotedPriceLabel: view.priceLabel,
        websiteUrl: view.websiteUrl,
        // Only a supplier-stated commitment counts as a quote — scraped
        // research prices stay out of the quote card.
        quote:
          sup?.quoteSource === 'SUPPLIER_QUOTED' && typeof sup?.quotedPrice === 'number' && sup.quotedPrice > 0
            ? {
                unitPrice: sup.quotedPrice,
                currency: typeof sup.currency === 'string' ? sup.currency : 'INR',
                leadTimeWeeks: typeof sup.leadTimeWeeks === 'number' ? sup.leadTimeWeeks : undefined,
                moq: typeof sup.moq === 'number' ? sup.moq : undefined,
                quotedAtIso: sup.quotedAt || undefined,
                rounds: Array.isArray(sup.changeHistory) ? sup.changeHistory.length : 0,
              }
            : undefined,
        negotiationAngles: Array.isArray(sup?.negotiationAngles) && sup.negotiationAngles.length > 0
          ? sup.negotiationAngles
          : undefined,
        negotiableToPrice: typeof sup?.negotiableTo?.price === 'number' ? sup.negotiableTo.price : undefined,
        specMismatches: view.specMismatches,
        specDecision: view.specDecision ?? null,
        // The commitment ledger, as stored — the change ribbon renders these
        // deltas under the reply that caused them.
        changeHistory: ledgerForThread(sup?.changeHistory, threadId),
        commitments: ledgerForThread(sup?.commitments, threadId),
        extractionStatus: latestConv?.quoteExtraction?.status,
      });
    });
  });
  return list;
};
